import React, { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { toast } from "react-toastify";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import { changePassword } from "../api/workOrder";
import { updateProfile } from "../api/workOrder";

const Onboarding = () => {
  const { user, updateUser } = useContext(AuthContext);
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  // Password form
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [profileForm, setProfileForm] = useState({
    firstName: "",
    lastName: "",
    countryCode: "+1",
    phone: "",
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  useEffect(() => {
    if (!user) return;

    if (user.mustChangePassword === true) {
      setStep(1);
    } else if (!user.isSetupComplete) {
      setStep(2);
    } else {
      navigate("/dashboard", { replace: true });
    }
  }, [user, navigate]);

  const validatePassword = () => {
    const errs = {};
    if (!passwordForm.currentPassword.trim())
      errs.currentPassword = "Current password is required";
    if (!passwordForm.newPassword.trim())
      errs.newPassword = "New password is required";
    else if (passwordForm.newPassword.length < 8)
      errs.newPassword = "Must be at least 8 characters";
    if (passwordForm.newPassword !== passwordForm.confirmPassword)
      errs.confirmPassword = "Passwords do not match";
    if (!passwordForm.confirmPassword.trim())
      errs.confirmPassword = "Please confirm your new password";
    return errs;
  };

  const validateProfile = () => {
    const errs = {};
    if (!profileForm.firstName.trim()) errs.firstName = "First name is required";
    if (!profileForm.lastName.trim()) errs.lastName = "Last name is required";
    const digits = profileForm.phone.replace(/\D/g, "");
    if (!digits) errs.phone = "Phone number is required";
    if (digits.length !== 10) errs.phone = "Phone must be exactly 10 digits";
    return errs;
  };

  const showAllErrors = (isPasswordStep) => {
    const validationFn = isPasswordStep ? validatePassword : validateProfile;
    const formData = isPasswordStep ? passwordForm : profileForm;

    const allTouched = {};
    Object.keys(formData).forEach((key) => {
      allTouched[key] = true;
    });
    setTouched((prev) => ({ ...prev, ...allTouched }));

    setErrors(validationFn());
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({ ...prev, [name]: value }));
    if (touched[name]) {
      const errs = validatePassword();
      setErrors((prev) => ({ ...prev, [name]: errs[name] }));
    }
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    let clean = value;
    if (name === "phone") clean = value.replace(/\D/g, "").slice(0, 10);
    setProfileForm((prev) => ({ ...prev, [name]: clean }));
    if (touched[name]) {
      const errs = validateProfile();
      setErrors((prev) => ({ ...prev, [name]: errs[name] }));
    }
  };

  const handleBlur = (e, isPassword = true) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));

    const validationFn = isPassword ? validatePassword : validateProfile;
    const errs = validationFn();
    setErrors((prev) => ({ ...prev, [name]: errs[name] }));
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    const errs = validatePassword();
    setErrors(errs);

    // Touch all fields to show errors under every field
    showAllErrors(true);

    if (Object.keys(errs).length > 0) {
      toast.error("Please fix the errors in the form");
      return;
    }

    setSubmitting(true);
    try {
      await changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });

      updateUser({ ...user, mustChangePassword: false });
      toast.success("Password changed successfully!");
      setStep(2);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to change password");
    } finally {
      setSubmitting(false);
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();

    const errs = validateProfile();
    setErrors(errs);

    // Touch all fields
    showAllErrors(false);

    if (Object.keys(errs).length > 0) {
      toast.error("Please fix the errors in the form");
      return;
    }

    setSubmitting(true);
    try {
      const fullPhone = `${profileForm.countryCode}${profileForm.phone}`;
      const res = await updateProfile({
        firstName: profileForm.firstName.trim(),
        lastName: profileForm.lastName.trim(),
        phone: fullPhone,
      });

      updateUser({
        ...user,
        ...res?.data,
        isSetupComplete: true,
      });

      toast.success("Profile completed successfully!");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to complete profile");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-12">
      <div className="bg-white shadow-2xl rounded-2xl overflow-hidden w-full max-w-lg transition-all duration-300">
        {/* Header */}
        <div className="bg-gray-900 text-white py-6 px-8">
          <h2 className="text-2xl font-bold text-center">
            {step === 1 ? "Let’s Secure Your Account" : "You're Almost There"}
          </h2>
          <p className="text-gray-400 text-center mt-2 text-sm">
            {step === 1
              ? "Create a new password to keep your account safe"
              : "Tell us a little about yourself to continue"}
          </p>
        </div>

        {/* Step Indicator */}
        <div className="px-8 pt-6 pb-4">
          <div className="flex items-center justify-between mb-6">
            <div className="flex-1">
              <div
                className={`h-1.5 rounded-full transition-all duration-500 ${
                  step >= 1 ? "bg-black" : "bg-gray-200"
                }`}
              />
              <p className="text-xs text-center mt-2 font-medium text-gray-700">
                Change Password
              </p>
            </div>
            <div className="w-8 h-1 bg-gray-200 mx-2" />
            <div className="flex-1">
              <div
                className={`h-1.5 rounded-full transition-all duration-500 ${
                  step >= 2 ? "bg-black" : "bg-gray-200"
                }`}
              />
              <p className="text-xs text-center mt-2 font-medium text-gray-700">
                Complete Profile
              </p>
            </div>
          </div>
        </div>

        {/* Form Content */}
        <div className="px-8 pb-10">
          {step === 1 ? (
            <form onSubmit={handlePasswordSubmit} className="space-y-6">
              {/* Current Password */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Current Password
                </label>
                <input
                  type={showCurrentPassword ? "text" : "password"}
                  name="currentPassword"
                  value={passwordForm.currentPassword}
                  onChange={handlePasswordChange}
                  onBlur={(e) => handleBlur(e, true)}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition ${
                    errors.currentPassword
                      ? "border-red-500 focus:ring-red-500"
                      : "border-gray-300 focus:ring-gray-700"
                  }`}
                  disabled={submitting}
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-10 text-gray-500 hover:text-gray-700"
                >
                  {showCurrentPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
                {errors.currentPassword && (
                  <p className="text-red-600 text-xs mt-1">
                    {errors.currentPassword}
                  </p>
                )}
              </div>

              {/* New Password */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <input
                  type={showNewPassword ? "text" : "password"}
                  name="newPassword"
                  value={passwordForm.newPassword}
                  onChange={handlePasswordChange}
                  onBlur={(e) => handleBlur(e, true)}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition ${
                    errors.newPassword
                      ? "border-red-500 focus:ring-red-500"
                      : "border-gray-300 focus:ring-gray-700"
                  }`}
                  disabled={submitting}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-10 text-gray-500 hover:text-gray-700"
                >
                  {showNewPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
                {errors.newPassword && (
                  <p className="text-red-600 text-xs mt-1">
                    {errors.newPassword}
                  </p>
                )}
              </div>

              {/* Confirm Password */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm New Password
                </label>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  value={passwordForm.confirmPassword}
                  onChange={handlePasswordChange}
                  onBlur={(e) => handleBlur(e, true)}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition ${
                    errors.confirmPassword
                      ? "border-red-500 focus:ring-red-500"
                      : "border-gray-300 focus:ring-gray-700"
                  }`}
                  disabled={submitting}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-10 text-gray-500 hover:text-gray-700"
                >
                  {showConfirmPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
                {errors.confirmPassword && (
                  <p className="text-red-600 text-xs mt-1">
                    {errors.confirmPassword}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={submitting}
                className={`w-full py-3.5 mt-6 bg-black text-white font-medium rounded-lg transition
                  ${submitting ? "opacity-60 cursor-not-allowed" : "hover:bg-gray-800"}`}
              >
                {submitting ? "Updating..." : "Continue"}
              </button>
            </form>
          ) : (
            <form onSubmit={handleProfileSubmit} className="space-y-6">
              {/* First & Last Name */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={profileForm.firstName}
                    onChange={handleProfileChange}
                    onBlur={(e) => handleBlur(e, false)}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition ${
                      errors.firstName ? "border-red-500 focus:ring-red-500" : "border-gray-300 focus:ring-gray-700"
                    }`}
                    disabled={submitting}
                  />
                  {errors.firstName && (
                    <p className="text-red-600 text-xs mt-1">{errors.firstName}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={profileForm.lastName}
                    onChange={handleProfileChange}
                    onBlur={(e) => handleBlur(e, false)}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition ${
                      errors.lastName ? "border-red-500 focus:ring-red-500" : "border-gray-300 focus:ring-gray-700"
                    }`}
                    disabled={submitting}
                  />
                  {errors.lastName && (
                    <p className="text-red-600 text-xs mt-1">{errors.lastName}</p>
                  )}
                </div>
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <div className="flex gap-3">
                  <select
                    name="countryCode"
                    value={profileForm.countryCode}
                    onChange={handleProfileChange}
                    className="w-28 px-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-700 bg-white"
                    disabled={submitting}
                  >
                    <option value="+1">+1 (US)</option>
                    <option value="+44">+44 (UK)</option>
                    <option value="+91">+91 (IN)</option>
                  </select>

                  <input
                    name="phone"
                    type="tel"
                    value={profileForm.phone}
                    onChange={handleProfileChange}
                    onBlur={(e) => handleBlur(e, false)}
                    placeholder="1234567890"
                    maxLength={10}
                    className={`flex-1 px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition ${
                      errors.phone ? "border-red-500 focus:ring-red-500" : "border-gray-300 focus:ring-gray-700"
                    }`}
                    disabled={submitting}
                  />
                </div>
                {errors.phone && (
                  <p className="text-red-600 text-xs mt-1">{errors.phone}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={submitting}
                className={`w-full py-3.5 mt-6 bg-black text-white font-medium rounded-lg transition
                  ${submitting ? "opacity-60 cursor-not-allowed" : "hover:bg-gray-800"}`}
              >
                {submitting ? "Saving..." : "Finish Setup"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Onboarding;